import * as ExpoClipboard from 'expo-clipboard';
import { useRef, useState } from 'react';
import { Pressable, TextInput } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import useAutoFocusAfterTransition from '@/hooks/useAutoFocusAfterTransition';
import Box from '../UI/Box';

type CodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  isDisabled?: boolean;
  autoFocus?: boolean;
};

export default function CodeInput({
  value,
  onChange,
  length = 8,
  isDisabled,
  autoFocus,
}: CodeInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<TextInput | null>(null);
  useAutoFocusAfterTransition(inputRef, autoFocus);

  const characterSlots = Array.from({ length }, (_, slotIndex) => ({
    id: `code-char-${slotIndex}`,
    index: slotIndex,
    value: value[slotIndex],
  }));

  const handleLongPress = async () => {
    if (process.env.EXPO_OS !== 'web' && !isDisabled) {
      const clipboardContent = await ExpoClipboard.getStringAsync();
      const formatted = clipboardContent
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .substring(0, length);
      onChange(formatted);
    }
  };

  return (
    <Pressable
      onPress={() => inputRef.current?.focus()}
      onLongPress={handleLongPress}
      disabled={isDisabled}
    >
      <Box direction="horizontal" space="sm" justifyContent="center">
        {characterSlots.map((slot) => (
          <Box
            key={slot.id}
            style={style.characterContainer({
              isFocused,
              index: slot.index,
              valueLength: value.length,
              length,
            })}
            alignItems="center"
            justifyContent="center"
          >
            <Text size="2xl" bold>
              {slot.value}
            </Text>
          </Box>
        ))}
      </Box>
      <TextInput
        testID="CodeInput"
        style={style.input}
        ref={inputRef}
        autoCorrect={false}
        value={value}
        onChangeText={(text) => {
          if (text.length <= length && !isDisabled) {
            onChange(text.toUpperCase().replace(/[^A-Z0-9]/g, ''));
          }
        }}
        maxLength={length}
        enterKeyHint="done"
        keyboardType="default"
        autoCapitalize="characters"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        editable={!isDisabled}
        pointerEvents="none"
      />
    </Pressable>
  );
}

const style = StyleSheet.create((theme) => ({
  characterContainer: ({
    isFocused,
    index,
    valueLength,
    length,
  }: {
    isFocused: boolean;
    index: number;
    valueLength: number;
    length: number;
  }) => ({
    width: theme.size(8),
    height: theme.size(12),
    borderBottomWidth: theme.size(0.5),
    borderBottomColor:
      isFocused &&
      (index === valueLength ||
        (index === length - 1 && valueLength === length))
        ? theme.colors.primary
        : theme.colors.borderMuted,
  }),
  input: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    padding: 0,
    margin: 0,
    borderWidth: 0,
    opacity: 0,
    zIndex: 1,
  },
}));
